// =============================================================================
// src/data/analytics-overlay-ingestion.ts — Config REAL de ingestão → UI.
//
// Builder puro (padrão sentinel-overlay): linhas cruas do Hasura (021 —
// config.watch_query, v_ingestion_*) viram o shape da aba Configurações.
// Tudo somente-leitura: o Analytics ESPELHA o que o Dagster usa; editar
// continua sendo curadoria/migration (banco vence o código, migration 008).
// =============================================================================

import type {
  AnalyticsIngestionCadenceRow,
  AnalyticsIngestionGraphqlData,
  AnalyticsIngestionProfileRow,
  AnalyticsIngestionScopeTermRow,
  AnalyticsIngestionOutletScopeRow,
  AnalyticsWatchQueryConfigRow,
} from './analytics-live-query';
import type {
  OmarAnalyticsIngestionCadence,
  OmarAnalyticsIngestionConfig,
  OmarAnalyticsIngestionProfileGroup,
  OmarAnalyticsIngestionTheme,
  OmarAnalyticsOutletScopeGroup,
  OmarAnalyticsScopeTermGroup,
} from '../features/analytics/analytics-types';
import { analyticsPlatformUiLabelFor } from './analytics-overlay-format';

/** Ordem editorial dos papéis (mesma leitura do kickoff: principal → cenário). */
const INGESTION_ROLE_LABELS: readonly (readonly [string, string])[] = [
  ['self', 'Monitorado principal'],
  ['ally', 'Aliados'],
  ['adversary', 'Adversarios'],
  ['unclassified', 'Cenario politico (sem classificacao)'],
  ['media', 'Midia e portais'],
  ['influencer', 'Influenciadores'],
  ['institution', 'Instituicoes'],
];

const INGESTION_KIND_LABELS: Readonly<Record<string, string>> = {
  profile_posts: 'Perfis monitorados (posts)',
  content_comments: 'Comentarios (2o estagio)',
  keyword_search: 'Busca paga por palavra-chave',
  serp_query: 'Descoberta SERP',
  news_rss: 'Noticias (RSS)',
  profile_details: 'Detalhes de perfil',
};

const INGESTION_SCOPE_KIND_LABELS: Readonly<Record<string, string>> = {
  anchor_geo: 'Ancoras geograficas (AM)',
  anchor_geo_weak: 'Ancoras geograficas fracas (so contexto)',
  anchor_institution: 'Instituicoes do AM',
  anchor_political_am: 'Figuras politicas do AM',
  out_of_scope: 'Termos de descarte',
  boilerplate: 'Assinaturas de portal (ignoradas)',
  reaction_lexicon: 'Lexico de reacao (comentario sem substancia)',
};

const INGESTION_OUTLET_SCOPE_LABELS: Readonly<Record<string, string>> = {
  local_am: 'Veiculos locais (AM)',
  national: 'Veiculos nacionais',
  other_state: 'Veiculos de outro estado',
};

const INGESTION_SAMPLE_SIZE = 8;

/** Monta o espelho completo da ingestão; sem temas E sem perfis = undefined (mock fica). */
export function buildAnalyticsIngestionPatch(
  ingestion: AnalyticsIngestionGraphqlData,
): OmarAnalyticsIngestionConfig | undefined {
  if (ingestion.watchQueries.length === 0 && ingestion.profiles.length === 0) return undefined;
  return {
    themes: ingestion.watchQueries.map(analyticsIngestionThemeFromRow),
    profileGroups: analyticsIngestionProfileGroups(ingestion.profiles),
    cadences: ingestion.cadences.map(analyticsIngestionCadenceFromRow),
    scopeTermGroups: analyticsIngestionScopeGroups(ingestion.scopeTerms),
    outletScopeGroups: analyticsIngestionOutletGroups(ingestion.outletScopes),
    blockedAuthors: ingestion.blockedAuthors.map((blockedRow) => ({
      platformLabel: analyticsPlatformUiLabelFor(blockedRow.platform, 'post'),
      handle: `@${blockedRow.author_handle}`,
    })),
  };
}

/** Cadência em linguagem de gente: 30 → "a cada 30 min", 720 → "a cada 12h". */
export function analyticsCadenceLabel(cadenceMin: number | null): string {
  if (cadenceMin == null || cadenceMin <= 0) return 'aguardando fila';
  if (cadenceMin < 60) return `a cada ${cadenceMin} min`;
  const hours = cadenceMin / 60;
  return Number.isInteger(hours) ? `a cada ${hours}h` : `a cada ${cadenceMin} min`;
}

function analyticsIngestionThemeFromRow(queryRow: AnalyticsWatchQueryConfigRow): OmarAnalyticsIngestionTheme {
  return {
    slug: queryRow.slug,
    themeLabel: queryRow.theme_label,
    terms: queryRow.terms,
    hashtags: queryRow.hashtags,
    platformLabels: queryRow.platforms.map((platform) => analyticsPlatformUiLabelFor(platform, 'post')),
    geo: queryRow.geo,
    isActive: queryRow.is_active,
    searchEnabled: queryRow.search_enabled,
    paidQueries: analyticsIngestionPaidQueries(queryRow),
  };
}

/** search_config por plataforma → rótulo legível ("#hashtag" ou a query crua). */
function analyticsIngestionPaidQueries(
  queryRow: AnalyticsWatchQueryConfigRow,
): OmarAnalyticsIngestionTheme['paidQueries'] {
  if (!queryRow.search_enabled) return [];
  return Object.entries(queryRow.search_config)
    .map(([platform, platformConfig]) => ({
      platformLabel: analyticsPlatformUiLabelFor(platform, 'post'),
      queryLabel: platformConfig.hashtag ? `#${platformConfig.hashtag}` : platformConfig.query ?? '',
    }))
    .filter((paidQuery) => paidQuery.queryLabel.length > 0);
}

function analyticsIngestionProfileGroups(
  profileRows: readonly AnalyticsIngestionProfileRow[],
): readonly OmarAnalyticsIngestionProfileGroup[] {
  return INGESTION_ROLE_LABELS
    .map(([role, roleLabel]) => ({
      roleLabel,
      profiles: profileRows
        .filter((profileRow) => profileRow.actor_role === role)
        .map((profileRow) => ({
          actorName: profileRow.actor_name,
          platformLabel: analyticsPlatformUiLabelFor(profileRow.platform, 'post'),
          handle: `@${profileRow.handle}`,
          isActive: profileRow.is_active,
          cadenceLabel: profileRow.is_active ? analyticsCadenceLabel(profileRow.cadence_min) : 'pausado',
        })),
    }))
    .filter((profileGroup) => profileGroup.profiles.length > 0);
}

function analyticsIngestionCadenceFromRow(cadenceRow: AnalyticsIngestionCadenceRow): OmarAnalyticsIngestionCadence {
  return {
    kindLabel: INGESTION_KIND_LABELS[cadenceRow.target_kind] ?? cadenceRow.target_kind,
    platformLabel: analyticsPlatformUiLabelFor(cadenceRow.platform, 'post'),
    cadenceLabel: analyticsCadenceLabel(cadenceRow.cadence_min),
    activeTargets: cadenceRow.active_targets,
    totalTargets: cadenceRow.total_targets,
  };
}

function analyticsIngestionScopeGroups(
  termRows: readonly AnalyticsIngestionScopeTermRow[],
): readonly OmarAnalyticsScopeTermGroup[] {
  const activeRows = termRows.filter((termRow) => termRow.is_active);
  const kinds = [...new Set(activeRows.map((termRow) => termRow.scope_kind))];
  return kinds.map((scopeKind) => {
    const kindTerms = activeRows.filter((termRow) => termRow.scope_kind === scopeKind);
    return {
      kindLabel: INGESTION_SCOPE_KIND_LABELS[scopeKind] ?? scopeKind,
      termCount: kindTerms.length,
      sampleTerms: kindTerms.slice(0, INGESTION_SAMPLE_SIZE).map((termRow) => termRow.term),
    };
  });
}

function analyticsIngestionOutletGroups(
  outletRows: readonly AnalyticsIngestionOutletScopeRow[],
): readonly OmarAnalyticsOutletScopeGroup[] {
  const scopes = [...new Set(outletRows.map((outletRow) => outletRow.outlet_scope))];
  return scopes.map((outletScope) => {
    const scopeOutlets = outletRows.filter((outletRow) => outletRow.outlet_scope === outletScope);
    return {
      scopeLabel: INGESTION_OUTLET_SCOPE_LABELS[outletScope] ?? outletScope,
      outletCount: scopeOutlets.length,
      sampleOutlets: scopeOutlets.slice(0, INGESTION_SAMPLE_SIZE).map((outletRow) => outletRow.outlet_handle),
    };
  });
}

/** Mapa handle→escopo p/ a coluna "Escopo" da tabela de Sites (021). */
export function analyticsOutletScopeLabelByHandle(
  outletRows: readonly AnalyticsIngestionOutletScopeRow[],
): ReadonlyMap<string, string> {
  const labelByHandle = new Map<string, string>();
  for (const outletRow of outletRows) {
    labelByHandle.set(outletRow.outlet_handle.toLowerCase(), analyticsOutletScopeShortLabel(outletRow.outlet_scope));
  }
  return labelByHandle;
}

/** Rótulo curto do escopo do veículo (célula da tabela de Sites). */
export function analyticsOutletScopeShortLabel(outletScope: string): string {
  if (outletScope === 'local_am') return 'Local (AM)';
  if (outletScope === 'national') return 'Nacional';
  if (outletScope === 'other_state') return 'Outro estado';
  return outletScope;
}
